import React, { useCallback, useMemo, useState, useEffect } from 'react';
import TextField from 'components/TextField';
import Form from 'components/Form';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Col, Row } from 'reactstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import {
  useApiGet,
  useApiPost,
  useApiPut,
  useApiDelete,
  useApiPostFile
} from 'hooks/useApi';
import Modal from 'components/Modal';
import { Loader } from 'react-bootstrap-typeahead';
import FileField from 'components/FileField';
import AdminNotification from 'models/AdminNotification';
import ToastType from 'enum/ToastType';

const schema = yup
  .object({
    title: yup.string().required('O título é obrigatório.'),
    message: yup.string().required('A descrição é obrigatória.'),
    image: yup.mixed()
  })
  .required();

const AdminNotificationsFormPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const { data, isLoading: isFetching } = useApiGet<AdminNotification>(
    `/admin/notifications`,
    { id: params.id, enabled: !!params.id }
  );
  const { mutateAsync: mutateCreate, isPending: isCreating } = useApiPost<
    AdminNotification
  >('/admin/notifications');
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useApiPut<
    AdminNotification
  >('/admin/notifications', params.id);

  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    '/admin/notifications',
    params.id
  );

  const { mutateAsync: uploadImage } = useApiPostFile(`/admin/notifications`);

  const mutate = params.id ? mutateUpdate : mutateCreate;

  const isSaving = isCreating || isUpdating;

  const adminNotifications = params.id ? data : undefined;

  const formMethods = useForm({
    resolver: yupResolver(schema)
  });

  const selectedImageFile = useWatch({
    control: formMethods.control,
    name: 'image',
    defaultValue: null
  });

  const onError = useFieldSetErrors(formMethods.setError);
  const { addToast } = useToast();

  const onSubmitHandler = useCallback(
    async (data: AdminNotification) => {
      try {
        const { image, ...dataWithoutImage } = data;

        const updatedData = {
          ...dataWithoutImage
        };

        const result = await mutate(updatedData as any);

        if (selectedImageFile) {
          const formData = new FormData();
          formData.append('image', selectedImageFile);
          formData.append('id', `${result.id}/image`);

          await uploadImage(formData as any);

          addToast(ToastType.SUCCESS, 'Logo salva com sucesso');
          navigate('/admin/notifications');
        }
        addToast(ToastType.SUCCESS, 'Notícia salva com sucesso');
        navigate('/admin/notifications');
      } catch (error) {
        console.error('Erro ao chamar mutate:', error);
        onError(error);
        addToast(ToastType.ERROR, 'Erro ao salvar notícia');
      }
    },
    [mutate, uploadImage, selectedImageFile, addToast, navigate, onError]
  );

  const handleOnDelete = useCallback(() => {
    if (!params.id) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir notícia'
        );
      },
      onSuccess: () => {
        addToast(ToastType.SUCCESS, 'Notícia excluída com sucesso');
        navigate('/admin/notifications');
      }
    });
  }, [addToast, mutateDelete, navigate, params.id]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    formMethods.setValue('image', file as Blob);
  };

  const imageSrc = useMemo(() => {
    return selectedImageFile
      ? URL.createObjectURL(new Blob([selectedImageFile]))
      : adminNotifications?.image;
  }, [selectedImageFile, adminNotifications?.image]);

  useEffect(() => {
    if (adminNotifications) {
      formMethods.reset({
        title: adminNotifications.title,
        message: adminNotifications.message
      } as any);
    }
  }, [adminNotifications, formMethods]);

  return (
    <div className="padding-page">
      <Form onSubmit={onSubmitHandler} {...formMethods}>
        <Row className="d-flex flex-column flex-md-row">
          <Col>
            <p className="text-gray-600 mb-1">
              <Link
                to="/admin/notifications"
                className="text-gray-600 text-decoration-none"
              >
                Notificações
              </Link>
              {' / '}
              {params.id ? 'Editar' : 'Nova'}
            </p>
            <h4 className="fw-bold mb-1">
              {params.id ? 'Editar Notificação' : 'Nova Notificação'}
            </h4>
            <span className="small-typography text-gray-600">
              {params.id
                ? 'Atualize os dados da notificação.'
                : 'Preencha os dados para criar uma nova notificação.'}
            </span>
          </Col>
          <Col className="d-flex gap-2 justify-content-end align-items-start mt-3 mt-md-0">
            {params.id && (
              <Button
                type="button"
                color="danger"
                loading={isDeleting}
                onClick={() => setShowDeleteConfirmation(true)}
              >
                Excluir
              </Button>
            )}
            <Button color="primary" loading={isSaving}>
              Salvar
            </Button>
          </Col>
        </Row>

        <div className="bg-white rounded shadow p-4 mt-4">
          {isFetching ? (
            <Loader />
          ) : (
            <>
              <Row>
                <Col md="6">
                  <TextField label="Título:" name="title" />
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <TextField
                    label="Descrição:"
                    name="message"
                    placeholder="Escreva o texto da notificação aqui"
                    type="textarea"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <label className="form-label">Imagem:</label>
                    <div className="d-flex align-items-center gap-3">
                      <FileField
                        name="image"
                        accept="image/*"
                        type="file"
                        onChange={handleImageChange}
                      />
                      {(selectedImageFile || adminNotifications?.image) && (
                        <img
                          width={100}
                          src={imageSrc}
                          alt="Preview"
                          className="rounded"
                        />
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      </Form>
      <Modal
        primaryButtonAction={handleOnDelete}
        primaryButtonLabel="Excluir"
        title="Atenção!"
        isOpen={showDeleteConfirmation}
        showCloseButton={false}
        toggle={() => setShowDeleteConfirmation((x) => !x)}
        secondaryButtonAction={() => setShowDeleteConfirmation(false)}
        secondaryButtonLabel="Cancelar"
      >
        <p>
          Deseja realmente excluir a notificação? <br />
          <small>Essa alteração não poderá ser revertida.</small>
        </p>
      </Modal>
    </div>
  );
};

export default AdminNotificationsFormPage;
